/**
 * AGROPORT ERP — Editor visual de plantillas de documentos.
 *
 * Canvas WYSIWYG que permite crear/editar layouts de Factura, Proforma, OC,
 * Guía de remisión, etc. Cada elemento (texto, rectángulo, imagen, tabla, variable)
 * se posiciona con drag-drop, se redimensiona con handles y se edita en el
 * panel de propiedades. El layout se serializa a JSON y se guarda en Mongo.
 * Un preview en PDF regenera el documento en cualquier momento.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import {
  TrashIcon, FloppyDiskIcon, ArrowLeftIcon, EyeIcon,
  TextTIcon, RectangleIcon, ImageIcon, TableIcon, TagIcon, CopyIcon,
  ArrowUUpLeftIcon, ArrowUUpRightIcon, LockIcon, StarIcon, FileMagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { ensureTemplateFonts, DEFAULT_FONTS, fontCss, PAPER } from "./_templates/fonts";
import { LockAlignBar, TextExtras, TableEditor } from "./_templates/ElementPropsExtra";
import PreviewRealDocModal from "./_templates/PreviewRealDocModal";

const DOC_TYPE_LABELS = {
  factura: "Factura",
  proforma: "Proforma",
  orden_compra: "Orden de Compra",
  guia_remision: "Guía de Remisión",
  recibo: "Recibo de Pago",
  nota_credito: "Nota de Crédito",
  nota_debito: "Nota de Débito",
  estado_cuenta: "Estado de Cuenta",
};

export default function TemplateEditor() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTipo = searchParams.get("tipo") || "factura";
  const isNew = !tid || tid === "new";
  const [tipo, setTipo] = useState(initialTipo);
  const [nombre, setNombre] = useState("Plantilla nueva");
  const [activo, setActivo] = useState(true);
  const [esPredeterminada, setEsPredeterminada] = useState(false);
  const [layout, setLayout] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [zoom, setZoom] = useState(0.7);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [fieldsCatalog, setFieldsCatalog] = useState([]);
  const [tables, setTables] = useState([]);
  const [fonts, setFonts] = useState(DEFAULT_FONTS);
  const [meta, setMeta] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [bgPdfUrl, setBgPdfUrl] = useState(null);
  const canvasRef = useRef(null);

  // Cargar catálogo de campos por tipo (§3.11)
  useEffect(() => {
    if (!tipo) return;
    api.get(`/document-templates/fields/${tipo}`)
      .then(({ data }) => { setFieldsCatalog(data.groups || []); setTables(data.tables || []); })
      .catch(() => { setFieldsCatalog([]); setTables([]); });
  }, [tipo]);

  // Tipografías (mismas en editor, vista previa y PDF)
  useEffect(() => {
    ensureTemplateFonts();
    api.get("/document-templates/fonts").then(({ data }) => setFonts(data.fonts || DEFAULT_FONTS)).catch(() => {});
  }, []);

  // Fondo PDF oficial del sistema (se muestra como imagen en el lienzo)
  const bgPdf = layout?.background_pdf;
  useEffect(() => {
    if (!bgPdf) { setBgPdfUrl(null); return undefined; }
    let url = null;
    api.get(`/document-templates/system-background/${bgPdf}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setBgPdfUrl(url); })
      .catch(() => setBgPdfUrl(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [bgPdf]);

  // Flechas del teclado: mover el elemento seleccionado con precisión (Shift = 10 px)
  useEffect(() => {
    const onKey = (e) => {
      if (!selectedId || ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) return;
      const d = e.shiftKey ? 10 : 1;
      const mv = { ArrowLeft: [-d, 0], ArrowRight: [d, 0], ArrowUp: [0, -d], ArrowDown: [0, d] }[e.key];
      if (!mv) return;
      e.preventDefault();
      setLayout((prev) => ({
        ...prev,
        elements: prev.elements.map((el) => (el.id === selectedId && !el.locked
          ? { ...el, x: Math.max(0, el.x + mv[0]), y: Math.max(0, el.y + mv[1]) } : el)),
      }));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedId]);

  useEffect(() => {
    (async () => {
      if (isNew) {
        const { data } = await api.get(`/document-templates/seed/${tipo}`);
        setLayout(data);
      } else {
        const { data } = await api.get(`/document-templates/${tid}`);
        setNombre(data.nombre); setTipo(data.tipo); setActivo(data.activo);
        setEsPredeterminada(!!data.es_predeterminada);
        setMeta({ usos: data.usos || 0, sistema: !!data.es_formato_sistema, version: data.version });
        setLayout(data.layout_json);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid]);

  const pushHistory = (l) => {
    setHistory((h) => [...h.slice(-19), JSON.stringify(l)]);
    setFuture([]);
  };

  const patch = (partial) => {
    setLayout((prev) => {
      pushHistory(prev);
      return { ...prev, ...partial };
    });
  };

  const patchElement = (id, elPatch) => {
    setLayout((prev) => {
      pushHistory(prev);
      return {
        ...prev,
        elements: prev.elements.map((e) => (e.id === id ? { ...e, ...elPatch } : e)),
      };
    });
  };

  const addElement = (type, extra = {}) => {
    const id = `e-${Date.now()}`;
    const base = { id, x: 60, y: 200, w: 200, h: 30, z: (layout.elements.length + 1) };
    const el =
      type === "text" ? { ...base, type: "text", text: "Texto nuevo", font_size: 11 }
      : type === "rect" ? { ...base, type: "rect", fill: "#e5e7eb", stroke: "#333333", stroke_width: 1 }
      : type === "image" ? { ...base, type: "image", src: "", w: 160, h: 80 }
      : type === "variable" ? { ...base, type: "variable", text: "{cliente_nombre}", var_key: "cliente_nombre", font_size: 11 }
      : type === "line" ? { ...base, type: "line", h: 0, stroke: "#000000", stroke_width: 1 }
      : type === "table" ? {
          ...base, type: "table", w: 716, h: 300,
          columns: [
            { key: "descripcion", label: "DESCRIPCIÓN", width_pct: 60, align: "left" },
            { key: "cantidad", label: "CANT.", width_pct: 15, align: "center" },
            { key: "total", label: "TOTAL", width_pct: 25, align: "right" },
          ],
        } : base;
    patch({ elements: [...layout.elements, { ...el, ...extra }] });
    setSelectedId(id);
  };

  const removeElement = (id) => {
    patch({ elements: layout.elements.filter((e) => e.id !== id) });
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateElement = (id) => {
    const src = layout.elements.find((e) => e.id === id);
    if (!src) return;
    const copy = { ...src, id: `e-${Date.now()}`, x: src.x + 20, y: src.y + 20 };
    patch({ elements: [...layout.elements, copy] });
    setSelectedId(copy.id);
  };

  const undo = () => {
    if (!history.length) return;
    const prev = history[history.length - 1];
    setFuture((f) => [JSON.stringify(layout), ...f]);
    setHistory((h) => h.slice(0, -1));
    setLayout(JSON.parse(prev));
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setHistory((h) => [...h, JSON.stringify(layout)]);
    setFuture((f) => f.slice(1));
    setLayout(JSON.parse(next));
  };

  const save = async (nuevaVersion = false) => {
    const payload = { tipo, nombre, layout, activo, es_predeterminada: esPredeterminada,
      publicar_nueva_version: nuevaVersion === true };
    try {
      if (isNew) {
        const { data } = await api.post("/document-templates", payload);
        toast.success("Plantilla creada");
        navigate(`/admin/plantillas/${data.id}`);
      } else {
        const { data } = await api.put(`/document-templates/${tid}`, payload);
        if (data.nueva_version) {
          toast.success(`Publicada la versión ${data.version}. La anterior quedó inactiva (se conserva).`);
          navigate(`/admin/plantillas/${data.id}`);
        } else {
          toast.success("Plantilla guardada");
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const preview = async () => {
    try {
      const resp = await api.post("/document-templates/preview-layout", layout, { responseType: "blob", params: { tipo } });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Error al generar preview");
    }
  };

  const uploadBackground = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/document-templates/upload-background", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Ajusta tamaño de la hoja al PDF/imagen oficial para alinear coordenadas
      setLayout((prev) => {
        pushHistory(prev);
        return {
          ...prev,
          background_image: data.data_uri,
          page_width: data.page_width_px || prev.page_width,
          page_height: data.page_height_px || prev.page_height,
        };
      });
      toast.success(data.source === "pdf" ? "PDF cargado como fondo oficial" : "Imagen cargada como fondo");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el archivo");
    }
  };

  const clearBackground = () => {
    if (!window.confirm("¿Quitar el PDF/imagen oficial de fondo?")) return;
    patch({ background_image: null });
  };

  const seedReset = async () => {
    if (!window.confirm("¿Reemplazar con el formato del sistema actual? Se perderán los cambios sin guardar.")) return;
    const { data } = await api.get(`/document-templates/seed/${tipo}`);
    patch({ elements: data.elements, background_pdf: data.background_pdf || null });
    setSelectedId(null);
  };

  if (!layout) {
    return <Layout><div className="p-8 text-center text-text-muted">Cargando editor…</div></Layout>;
  }

  const selected = layout.elements.find((e) => e.id === selectedId) || null;

  return (
    <Layout>
      <div className="flex h-[calc(100vh-80px)] gap-0 overflow-hidden">
        {/* Sidebar izquierda: herramientas */}
        <div className="w-56 bg-sidebar border-r border-border p-3 space-y-4 overflow-y-auto shrink-0">
          <div>
            <button onClick={() => navigate(tipo ? `/admin/plantillas/tipo/${tipo}` : "/admin/plantillas")}
                    className="text-xs text-text-muted hover:text-agro-green flex items-center gap-1"
                    data-testid="tpl-back">
              <ArrowLeftIcon size={12} /> Volver
            </button>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-muted mb-1 block">Nombre</label>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}
                   data-testid="tpl-name-input"
                   className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs" />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-muted mb-1 block">Tipo</label>
            <select value={tipo} disabled={!isNew} onChange={async (e) => {
                      setTipo(e.target.value);
                      if (isNew) {
                        const { data } = await api.get(`/document-templates/seed/${e.target.value}`);
                        setLayout(data);
                      }
                    }}
                    className="w-full bg-app border border-border rounded px-2 py-1.5 text-xs">
              {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Formato oficial</div>
            <label className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-[11px] bg-agro-green/10 border border-agro-green/40 text-agro-green rounded cursor-pointer hover:bg-agro-green/20"
                   data-testid="tpl-upload-bg-label">
              <ImageIcon size={14} />
              {layout.background_image ? "Cambiar PDF/imagen" : "Subir PDF oficial"}
              <input type="file" accept="application/pdf,image/*" className="hidden"
                     data-testid="tpl-upload-bg"
                     onChange={(e) => { uploadBackground(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {layout.background_image && (
              <>
                <div className="text-[10px] text-text-muted">Opacidad</div>
                <input type="range" min="0.1" max="1" step="0.05"
                       value={layout.background_opacity ?? 1}
                       onChange={(e) => patch({ background_opacity: parseFloat(e.target.value) })}
                       className="w-full" data-testid="tpl-bg-opacity" />
                <button onClick={clearBackground} data-testid="tpl-bg-clear"
                        className="w-full text-[11px] text-red-400 hover:bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5">
                  Quitar fondo
                </button>
              </>
            )}
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Agregar elemento</div>
            <ToolBtn onClick={() => addElement("text")} icon={TextTIcon} label="Texto" testid="add-text" />
            <ToolBtn onClick={() => addElement("variable")} icon={TagIcon} label="Variable" testid="add-var" />
            <ToolBtn onClick={() => addElement("rect")} icon={RectangleIcon} label="Rectángulo" testid="add-rect" />
            <ToolBtn onClick={() => addElement("line")} icon={RectangleIcon} label="Línea / separador" testid="add-line" />
            <ToolBtn onClick={() => addElement("image")} icon={ImageIcon} label="Imagen" testid="add-image" />
            <ToolBtn onClick={() => addElement("table")} icon={TableIcon} label="Tabla dinámica" testid="add-table" />
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Configuración de página</div>
            <label className="text-[10px] text-text-muted">Tamaño</label>
            <select value={layout.paper_size || "letter"}
                    onChange={(e) => {
                      if (e.target.value === "custom") { patch({ paper_size: "custom" }); return; }
                      const [w, h] = PAPER[e.target.value] || [816, 1056];
                      const isLand = (layout.orientation || "portrait") === "landscape";
                      patch({ paper_size: e.target.value,
                              page_width: isLand ? h : w,
                              page_height: isLand ? w : h });
                    }}
                    className="w-full bg-app border border-border rounded px-2 py-1 text-xs"
                    data-testid="tpl-paper-size">
              <option value="letter">Carta (Letter)</option>
              <option value="a4">A4</option>
              <option value="legal">Oficio (Legal)</option>
              <option value="custom">Personalizado</option>
            </select>
            <div className="grid grid-cols-2 gap-1">
              <input type="number" value={layout.page_width} title="Ancho de página (px a 96 dpi)" data-testid="tpl-page-w"
                     onChange={(e) => patch({ page_width: Number(e.target.value), paper_size: "custom" })}
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" />
              <input type="number" value={layout.page_height} title="Alto de página (px a 96 dpi)" data-testid="tpl-page-h"
                     onChange={(e) => patch({ page_height: Number(e.target.value), paper_size: "custom" })}
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" />
            </div>
            <div className="text-[9px] text-text-muted">{(layout.page_width / 96).toFixed(2)} x {(layout.page_height / 96).toFixed(2)} pulg.</div>
            <label className="text-[10px] text-text-muted">Orientación</label>
            <select value={layout.orientation || "portrait"}
                    onChange={(e) => {
                      const orient = e.target.value;
                      const w = layout.page_width, h = layout.page_height;
                      const needSwap = (orient === "landscape" && w < h) || (orient === "portrait" && w > h);
                      patch({ orientation: orient,
                              ...(needSwap ? { page_width: h, page_height: w } : {}) });
                    }}
                    className="w-full bg-app border border-border rounded px-2 py-1 text-xs"
                    data-testid="tpl-orientation">
              <option value="portrait">Vertical</option>
              <option value="landscape">Horizontal</option>
            </select>
            <label className="text-[10px] text-text-muted">Márgenes (px)</label>
            <div className="grid grid-cols-2 gap-1">
              <input type="number" value={layout.margin_top ?? 40}
                     onChange={(e) => patch({ margin_top: Number(e.target.value) })}
                     placeholder="Superior"
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" title="Margen superior" />
              <input type="number" value={layout.margin_bottom ?? 40}
                     onChange={(e) => patch({ margin_bottom: Number(e.target.value) })}
                     placeholder="Inferior"
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" title="Margen inferior" />
              <input type="number" value={layout.margin_left ?? 40}
                     onChange={(e) => patch({ margin_left: Number(e.target.value) })}
                     placeholder="Izquierdo"
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" title="Margen izquierdo" />
              <input type="number" value={layout.margin_right ?? 40}
                     onChange={(e) => patch({ margin_right: Number(e.target.value) })}
                     placeholder="Derecho"
                     className="bg-app border border-border rounded px-1 py-1 text-[10px]" title="Margen derecho" />
            </div>
            <label className="flex items-center gap-2 text-[10px] text-text-secondary cursor-pointer">
              <input type="checkbox"
                     checked={layout.repeat_table_headers !== false}
                     onChange={(e) => patch({ repeat_table_headers: e.target.checked })}
                     data-testid="tpl-repeat-headers" />
              Repetir encabezados en tabla multipágina
            </label>
          </div>

          {/* §3.11 – Panel de campos dinámicos por tipo */}
          {fieldsCatalog.length > 0 && (
            <div className="border-t border-border pt-3 space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">
                Campos disponibles ({DOC_TYPE_LABELS[tipo] || tipo})
              </div>
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1"
                data-testid="fields-catalog">
                {fieldsCatalog.filter((g) => g.grupo !== "Elementos manuales").map((g) => (
                  <div key={g.grupo}>
                    <div className="text-[9px] uppercase tracking-wider text-agro-green font-semibold mb-1">
                      {g.grupo}
                    </div>
                    <div className="grid grid-cols-1 gap-0.5">
                      {g.campos.map((c) => (
                        <button
                          key={c.key}
                          onClick={() => {
                            if (c.tipo === "table") {
                              addElement("table", { data_key: c.key, columns: (c.columnas || []).slice(0, 4).map((col, k) => ({
                                key: col.key, label: col.label.toUpperCase(), width_pct: k === 0 ? 40 : 20, align: k === 0 ? "left" : "right" })) });
                            } else {
                              addElement("variable", { var_key: c.key, text: `{${c.key}}` });
                            }
                          }}
                          data-testid={`field-${c.key}`}
                          className="text-left px-2 py-1 text-[10px] rounded hover:bg-agro-green/10 text-text-secondary hover:text-agro-green"
                          title={c.label}
                        >
                          <code className="text-[9px] text-agro-yellow">{c.tipo === "table" ? "tabla dinámica" : `{${c.key}}`}</code>
                          <div className="text-[10px] truncate">{c.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-border pt-3">
            <button onClick={seedReset} data-testid="tpl-seed"
                    className="w-full text-[11px] bg-agro-yellow/10 border border-agro-yellow/40 text-agro-yellow rounded px-2 py-1.5 hover:bg-agro-yellow/20">
              Cargar formato actual del sistema
            </button>
          </div>
          <p className="text-[10px] text-text-muted">Flechas del teclado: mover 1 px (Shift: 10 px).</p>
        </div>

        {/* Canvas central */}
        <div className="flex-1 flex flex-col bg-black/20 overflow-hidden">
          {/* Toolbar top */}
          <div className="flex items-center gap-2 p-2 border-b border-border bg-sidebar">
            <button aria-label="Deshacer" onClick={undo} disabled={!history.length}
                    className="p-1.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ArrowUUpLeftIcon size={16} /></button>
            <button aria-label="Rehacer" onClick={redo} disabled={!future.length}
                    className="p-1.5 text-text-muted hover:text-text-primary disabled:opacity-30"><ArrowUUpRightIcon size={16} /></button>
            <div className="w-px h-5 bg-border mx-1"></div>
            <label className="text-[10px] text-text-muted">Zoom</label>
            <input type="range" min="0.3" max="1.2" step="0.05" value={zoom}
                   onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-24" />
            <span className="text-[10px] text-text-muted">{Math.round(zoom * 100)}%</span>
            <div className="flex-1"></div>
            <label className="text-xs flex items-center gap-1.5 mr-2">
              <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)}
                     data-testid="tpl-active" className="accent-agro-green" />
              <span className="text-text-secondary">Activa</span>
            </label>
            <label className="text-xs flex items-center gap-1.5 mr-2">
              <input type="checkbox" checked={esPredeterminada}
                     onChange={(e) => {
                       if (e.target.checked && !activo) setActivo(true);
                       setEsPredeterminada(e.target.checked);
                     }}
                     data-testid="tpl-default" className="accent-agro-yellow" />
              <span className="text-text-secondary flex items-center gap-1">
                <StarIcon size={12} weight={esPredeterminada ? "fill" : "regular"}
                  className={esPredeterminada ? "text-agro-yellow" : "text-text-muted"} />
                Predeterminada
              </span>
            </label>
            <button onClick={preview} data-testid="tpl-preview"
                    className="text-xs bg-app border border-border rounded px-3 py-1.5 flex items-center gap-1.5 hover:bg-white/5">
              <EyeIcon size={14} /> Vista previa
            </button>
            <button onClick={() => setShowPreview(true)} data-testid="tpl-preview-real" title="Vista previa con un documento real"
                    className="text-xs bg-app border border-border rounded px-3 py-1.5 flex items-center gap-1.5 hover:bg-white/5">
              <FileMagnifyingGlassIcon size={14} /> Con documento
            </button>
            {!isNew && (
              <button onClick={() => save(true)} data-testid="tpl-publish"
                      title="Crea una nueva versión y deja la actual inactiva (se conserva)"
                      className="text-xs bg-app border border-agro-green/50 text-agro-green rounded px-3 py-1.5 hover:bg-agro-green/10">
                Publicar nueva versión
              </button>
            )}
            <button onClick={() => save(false)} data-testid="tpl-save"
                    className="text-xs bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded px-3 py-1.5 flex items-center gap-1.5">
              <FloppyDiskIcon size={14} /> Guardar
            </button>
          </div>

          {(meta.sistema || meta.usos > 0) && (
            <div className="px-3 py-1.5 text-[11px] bg-agro-yellow/10 text-agro-yellow border-b border-agro-yellow/30" data-testid="tpl-version-banner">
              {meta.sistema ? "Formato del sistema (renderer oficial). " : `Usada en ${meta.usos} documento(s). `}
              Al guardar cambios de diseño se publicará una nueva versión y la actual quedará inactiva.
            </div>
          )}
          {/* Canvas — el lienzo representa el área real de la página (px a 96 dpi) */}
          <div className="flex-1 overflow-auto p-8">
            <div
              ref={canvasRef}
              data-testid="tpl-canvas"
              onClick={(e) => { if (e.target === canvasRef.current) setSelectedId(null); }}
              style={{
                width: layout.page_width * zoom,
                height: layout.page_height * zoom,
                background: layout.background || "#FFFFFF",
                position: "relative",
                boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
                margin: "0 auto",
              }}
            >
              {layout.background_image && (
                <img
                  src={layout.background_image}
                  alt="Fondo oficial"
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: 0, top: 0,
                    width: "100%", height: "100%",
                    objectFit: "fill",
                    opacity: layout.background_opacity ?? 1,
                    pointerEvents: "none",
                    userSelect: "none",
                    zIndex: 0,
                  }}
                />
              )}
              {bgPdfUrl && (
                <img src={bgPdfUrl} alt="Formato oficial" draggable={false}
                  style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />
              )}
              <div style={{
                position: "absolute", pointerEvents: "none", zIndex: 1, border: "1px dashed rgba(22,163,74,0.35)",
                left: (layout.margin_left ?? 40) * zoom, top: (layout.margin_top ?? 40) * zoom,
                right: (layout.margin_right ?? 40) * zoom, bottom: (layout.margin_bottom ?? 40) * zoom,
              }} data-testid="tpl-margins" />
              {layout.elements.map((el) => (
                <ElementView
                  key={el.id}
                  el={el}
                  zoom={zoom}
                  selected={selectedId === el.id}
                  onSelect={() => setSelectedId(el.id)}
                  onChange={(p) => patchElement(el.id, p)}
                  onDelete={() => removeElement(el.id)}
                  onDuplicate={() => duplicateElement(el.id)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar derecha: propiedades */}
        <div className="w-72 bg-sidebar border-l border-border p-4 overflow-y-auto shrink-0">
          {selected ? (
            <PropertiesPanel el={selected} onChange={(p) => patchElement(selected.id, p)}
                             layout={layout} fields={fieldsCatalog} tables={tables} fonts={fonts}
                             onDelete={() => removeElement(selected.id)}
                             onDuplicate={() => duplicateElement(selected.id)} />
          ) : (
            <div className="text-xs text-text-muted">Seleccioná un elemento del canvas para editar sus propiedades.</div>
          )}
        </div>
      </div>
      {showPreview && (
        <PreviewRealDocModal template={{ id: tid, tipo, nombre }} layout={layout} onClose={() => setShowPreview(false)} />
      )}
    </Layout>
  );
}

function ToolBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-text-secondary bg-app border border-border rounded hover:border-agro-green hover:text-agro-green">
      <Icon size={14} /> {label}
    </button>
  );
}

/** Un elemento en el canvas: drag para mover, handle SE para redimensionar. */
function ElementView({ el, zoom, selected, onSelect, onChange, onDelete, onDuplicate }) {
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);

  const onMouseDown = (e) => {
    if (el.locked) return;
    e.stopPropagation();
    onSelect();
    setDrag({ startX: e.clientX, startY: e.clientY, elX: el.x, elY: el.y });
  };
  const onResizeDown = (e) => {
    e.stopPropagation();
    if (el.locked) return;
    onSelect();
    setResize({ startX: e.clientX, startY: e.clientY, elW: el.w, elH: el.h });
  };

  useEffect(() => {
    if (!drag && !resize) return;
    const onMove = (e) => {
      if (drag) {
        const dx = (e.clientX - drag.startX) / zoom;
        const dy = (e.clientY - drag.startY) / zoom;
        onChange({ x: Math.max(0, Math.round(drag.elX + dx)), y: Math.max(0, Math.round(drag.elY + dy)) });
      }
      if (resize) {
        const dw = (e.clientX - resize.startX) / zoom;
        const dh = (e.clientY - resize.startY) / zoom;
        onChange({ w: Math.max(20, Math.round(resize.elW + dw)), h: Math.max(10, Math.round(resize.elH + dh)) });
      }
    };
    const onUp = () => { setDrag(null); setResize(null); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, [drag, resize, zoom, onChange]);

  const commonStyle = {
    position: "absolute",
    left: el.x * zoom,
    top: el.y * zoom,
    width: el.w * zoom,
    height: el.h * zoom,
    cursor: el.locked ? "default" : (drag ? "grabbing" : "grab"),
    outline: selected ? "2px solid #16a34a" : (el.type === "rect" || el.type === "table" ? "none" : "1px dashed rgba(0,0,0,0.15)"),
    outlineOffset: 2,
    zIndex: (el.z || 0) + (selected ? 100 : 0),
  };

  const content = (() => {
    if (el.type === "text" || el.type === "variable") {
      // Misma métrica que el PDF: tamaño en pt (= px x 4/3), 1.ª línea base en y + tamaño + 2
      const size = Number(el.font_size || 10);
      const fs = size * 4 / 3;
      const lh = Number(el.line_height || size + 2);
      const top = (size + 2) - (lh - fs) / 2 - 0.86 * fs;
      return (
        <div style={{
          ...fontCss(el),
          fontSize: fs * zoom,
          lineHeight: `${lh * zoom}px`,
          marginTop: top * zoom,
          textDecoration: el.underline ? "underline" : "none",
          color: el.color || "#000000",
          textAlign: el.align || "left",
          overflow: "visible",
          whiteSpace: el.wrap === false ? "pre" : "pre-wrap",
          width: "100%",
        }}>
          {el.text}
        </div>
      );
    }
    if (el.type === "rect") {
      const sw = el.stroke && Number(el.stroke_width ?? 1) > 0 ? Number(el.stroke_width ?? 1) : 0;
      return <div style={{ background: el.fill || "transparent", border: sw ? `${sw * zoom}px solid ${el.stroke}` : "none", width: "100%", height: "100%", boxSizing: "border-box" }} />;
    }
    if (el.type === "line") {
      return <div style={{ borderTop: `${el.stroke_width || 1}px solid ${el.stroke || "#000"}`, width: "100%" }} />;
    }
    if (el.type === "image") {
      return el.src
        ? <img src={el.src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        : <div style={{ width: "100%", height: "100%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 10 * zoom }}>IMAGEN</div>;
    }
    if (el.type === "table") {
      const cols = el.columns || [];
      const hh = el.show_header === false ? 0 : Number(el.header_height || 22);
      const rh = Number(el.row_height || 18);
      const n = Math.max(0, Math.floor((el.h - hh) / rh));
      const fs = Number(el.font_size || 9) * 4 / 3 * zoom;
      const grid = el.show_grid !== false;
      return (
        <div style={{ width: "100%", height: "100%", fontSize: fs, ...fontCss({ font: el.font }), background: "rgba(255,255,255,0.35)",
          outline: "1px dashed rgba(22,163,74,0.6)", overflow: "hidden" }}>
          {hh > 0 && (
            <div style={{ display: "flex", height: hh * zoom, alignItems: "center", background: el.header_fill || "#F3F4F6",
              color: el.header_color || "#000", fontWeight: 700, border: grid ? "1px solid #333" : "none", boxSizing: "border-box" }}>
              {cols.map((c, k) => (
                <div key={k} style={{ width: `${c.width_pct}%`, padding: `0 ${4 * zoom}px`, textAlign: c.align || "left",
                  whiteSpace: "nowrap", overflow: "hidden" }}>{c.label}</div>
              ))}
            </div>
          )}
          {Array.from({ length: Math.min(n, 60) }).map((_, i) => (
            <div key={i} style={{ height: rh * zoom, borderBottom: grid ? "1px solid #ddd" : "none", color: "#9ca3af",
              display: "flex", alignItems: "center", paddingLeft: 4 * zoom }}>
              {i === 0 ? `— filas de ${el.data_key || "items"} (${n} por página; continúan en la siguiente) —` : ""}
            </div>
          ))}
        </div>
      );
    }
    return null;
  })();

  return (
    <div style={commonStyle} onMouseDown={onMouseDown} onClick={(e) => { e.stopPropagation(); onSelect(); }}
         data-testid={`el-${el.id}`}>
      {content}
      {selected && el.locked && (
        <div style={{ position: "absolute", top: -20, right: 0, color: "#ca8a04" }} title="Bloqueado"><LockIcon size={12} /></div>
      )}
      {selected && !el.locked && (
        <>
          <div style={{
            position: "absolute", right: -6, bottom: -6, width: 12, height: 12,
            background: "#16a34a", cursor: "nwse-resize", borderRadius: 2, border: "2px solid #fff",
          }} onMouseDown={onResizeDown} data-testid={`resize-${el.id}`} />
          <div style={{ position: "absolute", top: -22, left: 0, display: "flex", gap: 4 }}>
            <button onClick={(e) => { e.stopPropagation(); onDuplicate(); }} title="Duplicar"
                    style={{ background: "#16a34a", color: "white", border: 0, borderRadius: 3, padding: "2px 5px", fontSize: 10 }}>
              <CopyIcon size={10} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Borrar"
                    style={{ background: "#dc2626", color: "white", border: 0, borderRadius: 3, padding: "2px 5px", fontSize: 10 }}>
              <TrashIcon size={10} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const PropRow = ({ label, children }) => (
  <div className="mb-2.5">
    <label className="text-[10px] uppercase tracking-wider text-text-muted block mb-1">{label}</label>
    {children}
  </div>
);

function PropertiesPanel({ el, onChange, onDelete, onDuplicate, layout, fields, tables, fonts }) {
  const Prop = PropRow;
  const cls = "w-full bg-app border border-border rounded px-2 py-1 text-xs";
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-agro-green font-semibold">{el.type}</div>
        <div className="flex gap-1">
          <button onClick={onDuplicate} title="Duplicar" aria-label="Duplicar" className="p-1 text-text-muted hover:text-agro-green"><CopyIcon size={12} /></button>
          <button onClick={onDelete} title="Borrar" aria-label="Borrar" className="p-1 text-text-muted hover:text-red-400"><TrashIcon size={12} /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Prop label="X">
          <input type="number" value={el.x} onChange={(e) => onChange({ x: Number(e.target.value) })} className={cls} data-testid="prop-x" />
        </Prop>
        <Prop label="Y">
          <input type="number" value={el.y} onChange={(e) => onChange({ y: Number(e.target.value) })} className={cls} data-testid="prop-y" />
        </Prop>
        <Prop label="Ancho">
          <input type="number" value={el.w} onChange={(e) => onChange({ w: Number(e.target.value) })} className={cls} />
        </Prop>
        <Prop label="Alto">
          <input type="number" value={el.h} onChange={(e) => onChange({ h: Number(e.target.value) })} className={cls} />
        </Prop>
      </div>
      <LockAlignBar el={el} layout={layout} onChange={onChange} />

      {(el.type === "text" || el.type === "variable") && (
        <>
          <Prop label="Texto">
            <textarea rows="2" value={el.text} onChange={(e) => onChange({ text: e.target.value })} className={cls} data-testid="prop-text" />
          </Prop>
          <TextExtras el={el} onChange={onChange} fields={fields} />
          <div className="grid grid-cols-2 gap-2">
            <Prop label="Fuente">
              <select value={el.font || "Helvetica"} onChange={(e) => onChange({ font: e.target.value })} className={cls} data-testid="prop-font">
                {fonts.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}{f.available === false ? ` (no instalada: usa ${f.fallback || "Montserrat"})` : ""}</option>
                ))}
              </select>
            </Prop>
            <Prop label="Tamaño">
              <input type="number" step="0.5" value={el.font_size} onChange={(e) => onChange({ font_size: Number(e.target.value) })} className={cls} data-testid="prop-font-size" />
            </Prop>
            <Prop label="Color">
              <input type="color" value={el.color || "#000000"} onChange={(e) => onChange({ color: e.target.value })} className="w-full h-8 bg-app border border-border rounded" />
            </Prop>
            <Prop label="Alineación">
              <select value={el.align || "left"} onChange={(e) => onChange({ align: e.target.value })} className={cls}>
                <option value="left">Izquierda</option><option value="center">Centro</option><option value="right">Derecha</option>
              </select>
            </Prop>
          </div>
          <div className="grid grid-cols-3 gap-1 mb-2">
            <button onClick={() => onChange({ bold: !el.bold })} data-testid="btn-bold"
                    className={`text-xs px-2 py-1 border rounded font-bold ${el.bold ? "bg-agro-green/20 border-agro-green text-agro-green" : "border-border text-text-secondary"}`}>B</button>
            <button onClick={() => onChange({ italic: !el.italic })} data-testid="btn-italic"
                    className={`text-xs px-2 py-1 border rounded italic ${el.italic ? "bg-agro-green/20 border-agro-green text-agro-green" : "border-border text-text-secondary"}`}>I</button>
            <button onClick={() => onChange({ underline: !el.underline })} data-testid="btn-underline"
                    className={`text-xs px-2 py-1 border rounded underline ${el.underline ? "bg-agro-green/20 border-agro-green text-agro-green" : "border-border text-text-secondary"}`}>U</button>
          </div>
        </>
      )}

      {el.type === "rect" && (
        <>
          <Prop label="Relleno">
            <input type="color" value={el.fill || "#ffffff"} onChange={(e) => onChange({ fill: e.target.value })} className="w-full h-8 bg-app border border-border rounded" />
          </Prop>
          <label className="flex items-center gap-2 text-[11px] text-text-secondary mb-2">
            <input type="checkbox" checked={!el.fill} onChange={(e) => onChange({ fill: e.target.checked ? null : "#ffffff" })} /> Sin relleno
          </label>
          <Prop label="Borde">
            <input type="color" value={el.stroke || "#000000"} onChange={(e) => onChange({ stroke: e.target.value })} className="w-full h-8 bg-app border border-border rounded" />
          </Prop>
          <label className="flex items-center gap-2 text-[11px] text-text-secondary mb-2">
            <input type="checkbox" checked={!el.stroke} onChange={(e) => onChange({ stroke: e.target.checked ? null : "#000000", stroke_width: e.target.checked ? 0 : 1 })} /> Sin borde
          </label>
          <Prop label="Grosor borde">
            <input type="number" step="0.5" value={el.stroke_width || 1} onChange={(e) => onChange({ stroke_width: Number(e.target.value) })} className={cls} />
          </Prop>
        </>
      )}

      {el.type === "line" && (
        <>
          <Prop label="Color">
            <input type="color" value={el.stroke || "#000000"} onChange={(e) => onChange({ stroke: e.target.value })} className="w-full h-8 bg-app border border-border rounded" />
          </Prop>
          <Prop label="Grosor">
            <input type="number" step="0.5" value={el.stroke_width || 1} onChange={(e) => onChange({ stroke_width: Number(e.target.value) })} className={cls} />
          </Prop>
        </>
      )}

      {el.type === "table" && <TableEditor el={el} onChange={onChange} tables={tables} fonts={fonts} />}

      {el.type === "image" && (
        <Prop label="URL / Data URI">
          <textarea rows="3" value={el.src || ""} onChange={(e) => onChange({ src: e.target.value })}
                    placeholder="Pega una URL o data:image/...;base64,..." className={cls} data-testid="prop-image-src" />
          <input type="file" accept="image/*" onChange={(ev) => {
            const f = ev.target.files?.[0]; if (!f) return;
            const r = new FileReader();
            r.onload = () => onChange({ src: r.result });
            r.readAsDataURL(f);
          }} className="mt-2 text-[10px] text-text-muted" />
        </Prop>
      )}
    </div>
  );
}
